import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  faNewspaper,
  faUsers,
  faTrophy,
  faCog,
  faUserFriends,
  faServer,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import AnalyticsSummary from '../../components/admin/AnalyticsSummary';
import '../../styles/admin/admin-dashboard-page.scss';

interface DashboardCardProps {
  title: string;
  description: string;
  icon: IconDefinition;
  linkTo: string;
  linkText: string;
  cardType: string;
  isExternal?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  description,
  icon,
  linkTo,
  linkText,
  cardType,
  isExternal = false
}) => {
  return (
    <div className={`dashboard-card ${cardType}-card`}>
      <div className="card-icon">
        <FontAwesomeIcon icon={icon} size="2x" />
      </div>
      <div className="card-content">
        <h3>{title}</h3>
        <p>{description}</p>
        {isExternal ? (
          <a href={linkTo} className="card-action" target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        ) : (
          <Link to={linkTo} className="card-action">
            {linkText}
          </Link>
        )}
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  // Set page title
  React.useEffect(() => {
    document.title = 'Admin Dashboard | Northern Championships Series';
  }, []);
  // Card data
  const cards: DashboardCardProps[] = [
    {
      title: "News Articles",
      description: "Manage news articles, create new content, and edit existing publications",
      icon: faNewspaper,
      linkTo: "/admin/articles",
      linkText: "Manage Articles",
      cardType: "articles"
    },
    {
      title: "Users",
      description: "Manage user accounts, profiles, and administrator permissions",
      icon: faUserFriends,
      linkTo: "/admin/users",
      linkText: "Manage Users",
      cardType: "users"
    },
    {
      title: "Teams",
      description: "Manage teams, players, and tournament registrations",
      icon: faUsers,
      linkTo: "/admin/teams",
      linkText: "Manage Teams",
      cardType: "teams"
    },
    {
      title: "Tournaments",
      description: "Create and manage tournaments, brackets, and match results",
      icon: faTrophy,
      linkTo: "/admin/tournaments",
      linkText: "Manage Tournaments",
      cardType: "tournaments"
    },
    {
      title: "Servers",
      description: "Manage CS2 game servers, monitor status, and configure server settings",
      icon: faServer,
      linkTo: "/admin/servers",
      linkText: "Manage Servers",
      cardType: "servers"
    },
    {
      title: "Analytics",
      description: "View detailed site analytics, user engagement metrics, and traffic statistics",
      icon: faChartLine,
      linkTo: "https://analytics.ncseries.gg/share/StDE39q9c3WXjh78",
      linkText: "View Analytics",
      cardType: "analytics",
      isExternal: true
    },
    {
      title: "Settings",
      description: "Configure site settings, user permissions, and general options",
      icon: faCog,
      linkTo: "/admin/settings",
      linkText: "Site Settings",
      cardType: "settings"
    }
  ];

  return (
    <div className="admin-dashboard">
      <div className="container">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
        </div>

        {/* Analytics Summary Widget */}
        {/* <AnalyticsSummary /> */}

        <div className="dashboard-grid">
          {cards.map((card, index) => (
            <DashboardCard
              key={index}
              title={card.title}
              description={card.description}
              icon={card.icon}
              linkTo={card.linkTo}
              linkText={card.linkText}
              cardType={card.cardType}
              isExternal={card.isExternal}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;